import { orgChartClient } from 'myNet'

import orgChartDB from 'libs/DB/orgChartDB'

type ListenerFunction = (data: OrgChartData | null) => void

class ListenerObj {
  listeners: ListenerFunction[]

  constructor() {
    this.listeners = []
  }

  notify = (data: OrgChartData | null): void => {
    this.listeners.forEach((listener) => {
      listener(data)
    })
  }

  add = (listener: ListenerFunction): void => {
    if (this.listeners.indexOf(listener) < 0) {
      this.listeners.push(listener)
    }
  }

  remove = (listener: ListenerFunction): void => {
    const pos = this.listeners.indexOf(listener)
    if (pos >= 0) {
      this.listeners.splice(pos, 1)
    }
  }
}

type ErrorListenerFunction = () => void

// 저장 실패 신호 전용 리스너. 페이로드 없이 "실패했다"는 신호만 전달하므로
// ListenerObj(인자 필수)와 분리한다.
class ErrorListenerObj {
  listeners: ErrorListenerFunction[]

  constructor() {
    this.listeners = []
  }

  notify = (): void => {
    this.listeners.forEach((listener) => {
      listener()
    })
  }

  add = (listener: ErrorListenerFunction): void => {
    if (this.listeners.indexOf(listener) < 0) {
      this.listeners.push(listener)
    }
  }

  remove = (listener: ErrorListenerFunction): void => {
    const pos = this.listeners.indexOf(listener)
    if (pos >= 0) {
      this.listeners.splice(pos, 1)
    }
  }
}

interface DepartmentMember {
  user_id: string
  jobtitle_id: string
}

interface Department {
  id: string
  title: string
  name?: string
  position?: string
  members: DepartmentMember[]
}

interface JobPosition {
  id: string
  title: string
  name?: string
}

interface JobTitle {
  id: string
  title: string
  name?: string
  position?: string
  jobposition_id: string
}

interface Manager {
  id: string
  name: string
}

interface EmailItem {
  address: string
}

interface UserData {
  id?: string
  account?: string
  name?: string
  display_name?: string
  emails?: EmailItem[]
  profile_photo_url?: string
  organization_name?: string
  jobposition?: string
  mobiles?: any[]
  departmentData?: {
    id?: string
    title?: string
    name?: string
    position?: string
  }
  jobData?: {
    jobposition?: string
    jobtitle?: string
    position?: string
    jobposition_id?: string | null
  }
}

interface User {
  id: string
  data: UserData
}

interface OrgChartData {
  departments: Department[]
  jobpositions: JobPosition[]
  jobtitles: JobTitle[]
  managers: Manager[]
  users: User[]
}

interface DBResponse {
  data: any
}

interface AllDataResponse {
  dataList: User[]
}

interface SubData {
  department?: Department
  job?: JobTitle
  jobposition?: JobPosition
}

interface ApiResponse {
  origin?: {
    status: number
    headers: Headers
  }
  data?: {
    data?: OrgChartData
  }
}

// 불량 데이터로 저장 실패 시, 반복 재요청을 막는 서킷 브레이커 쿨다운(10분).
const RELOAD_BLOCK_COOLDOWN_MS = 10 * 60 * 1000

// TTL 캐시 수명(1분). 최근 성공 로드 후 이 시간 내 재호출은 네트워크를 건너뛰고
// 캐시만 표시한다. 조직도는 자주 변경되지 않으므로 넉넉히 둬서 반복 재요청을 줄인다.
// reply 폼의 동시 호출은 in-flight dedup이, 시차 재진입은 이 TTL이 막는다.
// manual 새로고침(bypassBreaker)은 TTL을 우회하므로 즉시 최신화가 필요하면 새로고침한다.
const LOAD_TTL_MS = 60_000

class OrgChartHelper {
  isLoaded: boolean
  changeListener: ListenerObj
  errorListener: ErrorListenerObj
  private lastNotifiedETag: string | null | undefined = undefined

  // 저장 실패 경고 래치: 최근 저장 실패 여부. 성공/로그아웃 시 false.
  saveFailureActive = false
  // 현재 실패 episode에 "경고 표시 완료" 가드. 성공/로그아웃/브라우저reload 시 false.
  saveFailureNotified = false
  // 서킷 브레이커: 이 시각 전까지 서버 재요청 차단. null=차단 없음.
  reloadBlockedUntil: number | null = null

  // in-flight dedup: 진행 중인 자동 startLoading의 Promise. 동시 호출(reply 폼의
  // To/Cc/Bcc + onReady 등)을 1개 네트워크 요청으로 합친다. manual 새로고침
  // (bypassBreaker)은 이 경로를 타지 않는다.
  private inflight: Promise<OrgChartData | null> | null = null
  // 단기 TTL 캐시: 마지막 성공 로드 시각. TTL 내 재호출은 네트워크를 건너뛰고
  // 캐시만 표시한다(동시 호출은 inflight가, 시차 호출은 TTL이 막는다).
  private lastLoadedAt = 0

  constructor() {
    this.isLoaded = false
    this.changeListener = new ListenerObj()
    this.errorListener = new ErrorListenerObj()
  }

  // ── 저장 실패/서킷 브레이커 제어 ─────────────────────────────
  // 불량 데이터 저장 실패 시: 경고 래치 설정 + 구독자(마운트된 Tree)에 즉시 표시 시도.
  markSaveFailure = (): void => {
    this.saveFailureActive = true
    this.errorListener.notify()
  }

  // 저장 성공(또는 계정 전환) 시: 경고/브레이커 상태를 모두 해제하고 재알림을 re-arm.
  clearSaveFailure = (): void => {
    this.saveFailureActive = false
    this.saveFailureNotified = false
    this.reloadBlockedUntil = null
  }

  // 불량 데이터 저장 실패 시 서버 재요청을 쿨다운 동안 차단.
  openReloadBreaker = (): void => {
    this.reloadBlockedUntil = Date.now() + RELOAD_BLOCK_COOLDOWN_MS
  }

  isReloadBlocked = (): boolean => {
    return this.reloadBlockedUntil != null && Date.now() < this.reloadBlockedUntil
  }

  // 로그아웃/세션 전환 시 조직도 싱글톤 상태를 초기화한다(교차계정/브레이커 잔존 방지).
  // DB clear는 logout()의 dataMgr.clear()가 이미 담당하므로 여기선 DB I/O를 하지 않는다
  // (ensureDB/open을 피해 로그아웃 중 blocked 팝업/hang/삭제경쟁을 원천 차단).
  logout = (): void => {
    this.isLoaded = false
    this.lastNotifiedETag = undefined
    this.saveFailureActive = false
    this.saveFailureNotified = false
    this.reloadBlockedUntil = null
    // in-flight/TTL 상태도 초기화한다. 계정 전환 후 이전 계정 데이터가 TTL로
    // 재사용되거나, 진행 중이던 요청에 새 세션 호출이 dedup되는 것을 막는다.
    this.inflight = null
    this.lastLoadedAt = 0
  }

  // 조직도 데이터 구조 유효성 검증. 빈 조직(전부 [])은 유효(무한 재요청 방지).
  private validateStructure = (data: OrgChartData): boolean => {
    if (!Array.isArray(data.departments)) return false
    if (!Array.isArray(data.jobpositions)) return false
    if (!Array.isArray(data.jobtitles)) return false
    if (!Array.isArray(data.managers)) return false
    if (!Array.isArray(data.users)) return false
    // department 원소가 null/비정상이어도 throw하지 않도록 옵셔널 체이닝으로 방어한다
    // (서버 응답 변형 대비 — 불량 데이터 방어가 이 함수의 목적이므로 여기서 죽으면 안 된다).
    const members = data.departments.reduce(
      (sum, department) => sum + (department?.members?.length ?? 0),
      0,
    )
    // 멤버는 있는데 사용자가 0명이면 서버 데이터 불일치 → 커밋 거부.
    if (members > 0 && data.users.length === 0) return false
    return true
  }

  // IndexedDB 초기화를 보장한다. loadData/startLoading 이전에 호출해야 캐시 읽기가
  // isActive=false로 조용히 null을 반환하는 것을 막는다.
  ensureDB = async (): Promise<boolean> => {
    if (this.isLoaded === false) {
      this.isLoaded = await orgChartDB.getDB()
    }
    return this.isLoaded
  }

  // 서버에서 조직도를 조건부(ETag) 요청해 캐시를 갱신한다. loadNewData 내부의 304
  // 자가회복도 이 경로 안에서 그대로 동작한다.
  //
  // TTL(lastLoadedAt)은 "로드 성공(ret != null) + 저장 성공(isLoaded === true)"일 때만
  // 시작한다. 200 응답을 받았어도 저장이 실패하면(불량 데이터/DB 비활성 등) saveData가
  // this.isLoaded=false 로 세팅하고 마지막에 stale 캐시(non-null)를 반환하므로, ret만으로
  // 판단하면 "저장은 실패했는데 TTL이 시작되어 재요청/DB 재초기화가 60초 지연"된다.
  // isLoaded 신호를 함께 보면 저장 실패 시 TTL을 시작하지 않아 다음 호출이 즉시 재시도한다.
  private fetchFromServer = async (): Promise<OrgChartData | null> => {
    let ret: OrgChartData | null = null
    try {
      const eTagInfo = await orgChartDB.getData('info_etag')
      console.log('eTagInfo : ', eTagInfo)
      ret = await this.loadNewData(eTagInfo?.data)
    } catch (e) {
      console.error('orgChartDB.getData error : ', e)
      ret = await this.loadNewData()
    }
    if (ret != null && this.isLoaded === true) {
      this.lastLoadedAt = Date.now()
    }
    return ret
  }

  startLoading = async (options?: { bypassBreaker?: boolean }): Promise<OrgChartData | null> => {
    // manual 새로고침(bypassBreaker): dedup/TTL/서킷 브레이커를 모두 우회해 즉시 재요청한다
    // (관리자가 데이터를 고친 직후 캐시/쿨다운을 기다리지 않도록). 진입점이므로 다음
    // loadData()에서 notify가 반드시 발화되도록 lastNotifiedETag를 리셋한다.
    if (options?.bypassBreaker === true) {
      this.lastNotifiedETag = undefined
      if ((await this.ensureDB()) !== true) {
        return null
      }
      return await this.fetchFromServer()
    }

    // in-flight dedup: 진행 중인 자동 요청이 있으면 그 Promise를 공유해 중복 네트워크를
    // 막는다(reply 폼의 To/Cc/Bcc + onReady 동시 호출 → 1회). 공유 호출자는
    // lastNotifiedETag 리셋을 건너뛴다 — 첫 호출자가 이미 리셋했고, 이들은 notify가 아니라
    // 반환값을 사용하므로 안전하다.
    if (this.inflight != null) {
      return this.inflight
    }

    // 명시적 리로드 진입점이므로 다음 loadData()에서 반드시 notify가 발화되도록 강제한다.
    // 이 장치가 없으면 findContact → loadData 경로가 먼저 lastNotifiedETag를 세팅한 뒤
    // Tree 리마운트/refresh 시 같은 eTag 비교로 notify가 억제되어 로딩 스피너가 잔류한다.
    // TTL hit 경로도 이 리셋 덕분에 loadData가 notify를 발화한다.
    this.lastNotifiedETag = undefined

    // try/finally로 inflight를 반드시 정리한다. 정리하지 않으면 예외 발생 시 inflight가
    // 영구 고착되어 이후 모든 startLoading이 끝나지 않는 Promise를 await하며 hang한다.
    this.inflight = (async (): Promise<OrgChartData | null> => {
      if ((await this.ensureDB()) !== true) {
        return null
      }

      // 불량 데이터 저장 실패로 브레이커가 열려 있으면 서버 재요청을 건너뛰고 캐시만 표시한다
      // (반복 200 재요청 방지). lastLoadedAt은 갱신하지 않는다(실제 로드가 아니므로).
      if (this.isReloadBlocked()) {
        console.warn('OrgChart: 저장 실패 쿨다운 중 — 서버 재요청 차단, 캐시만 표시')
        return await this.loadData()
      }

      // TTL hit: 최근 성공 로드 직후면 네트워크를 건너뛰고 캐시만 표시한다(notify는 loadData가
      // 발화). lastLoadedAt은 갱신하지 않아 TTL이 sliding 되지 않고 고정 윈도로 만료된다.
      if (Date.now() - this.lastLoadedAt < LOAD_TTL_MS) {
        return await this.loadData()
      }

      return await this.fetchFromServer()
    })().finally(() => {
      this.inflight = null
    })

    return this.inflight
  }

  saveData = async (data: OrgChartData | null, eTag?: string): Promise<OrgChartData | null> => {
    // data가 없으면 캐시를 절대 훼손하지 않는다(clear/etag 저장 안 함). 서버가 200 + Etag를
    // 주더라도 본문 data가 비면 기존 유효 캐시를 그대로 유지한다. 이 early-return이 없으면
    // clear() 후 info_etag만 저장되어, 다음 304 응답이 빈 캐시를 영구히 재사용하게 된다.
    if (data == null) {
      return null
    }

    const { departments, jobpositions, jobtitles, managers, users } = data

    try {
      // BaseDB 반환값: 성공=item/true, 비활성 no-op=null, 실제 쓰기 에러=reject.
      // clear/meta/user를 순차 await하고, 멱등한 putData로 멀티탭 ConstraintError(거짓 경고)를 피한다.
      const clearRes = await orgChartDB.clear()

      const dep = await orgChartDB.putData('info_departments', departments)
      const pos = await orgChartDB.putData('info_jobpositions', jobpositions)
      const tit = await orgChartDB.putData('info_jobtitles', jobtitles)
      const man = await orgChartDB.putData('info_managers', managers)

      const settled = await Promise.allSettled(
        users.map((user) => orgChartDB.putData(user.id, user)),
      )

      const clearOk = clearRes === true
      const metaOk = [dep, pos, tit, man].every((result) => result != null)
      const userOk = settled.every(
        (result) => result.status === 'fulfilled' && result.value != null,
      )
      const structureOk = this.validateStructure(data)

      // 실패 원인 분류(putData 성공은 절대 null 아님 → null은 오직 DB 비활성):
      //  - userRejected: 실제 쓰기 에러(불량 데이터/쿼터 등)
      //  - inactiveSeen: DB 비활성(버전 변경 등 일시적 상태)
      const userRejected = settled.some((result) => result.status === 'rejected')
      const inactiveSeen =
        clearRes === null ||
        [dep, pos, tit, man].some((result) => result == null) ||
        settled.some((result) => result.status === 'fulfilled' && result.value == null)

      const canCommit = clearOk && metaOk && userOk && structureOk

      // 저장이 완전하지 않으면 다음 ensureDB가 재초기화하도록 강제.
      if (!canCommit) {
        this.isLoaded = false
      }

      if (canCommit) {
        // info_etag는 "커밋 마커". 모든 저장이 완전히 성공한 경우에만 마지막에 저장한다.
        if (eTag != null) {
          await orgChartDB.putData('info_etag', eTag)
        }
        this.clearSaveFailure()
      } else if (userRejected || !structureOk) {
        // 불량 데이터: 경고(관리자 수정 유도) + 서버 재요청 차단(서킷 브레이커).
        console.warn('OrgChart saveData: 불량 데이터로 저장 실패 — etag 미커밋, 경고/차단')
        this.markSaveFailure()
        this.openReloadBreaker()
      } else if (inactiveSeen) {
        // 일시적 DB 비활성: 경고/차단 없이 재초기화만 유도(versionChangedPopup이 이미 안내).
        console.warn('OrgChart saveData: DB 비활성으로 저장 skip — 재초기화 예정')
      }
    } catch (e) {
      // clear/meta throw: DB 이상 또는 쓰기 에러 → 불량 데이터에 준해 경고 + 차단.
      this.isLoaded = false
      this.markSaveFailure()
      this.openReloadBreaker()
      console.error('OrgChart saveData error:', e)
    }

    // 직접 notify 금지 — loadData의 lastNotifiedETag dedup 경로를 그대로 사용한다.
    return await this.loadData()
  }

  loadData = async (options?: { force?: boolean }): Promise<OrgChartData | null> => {
    try {
      const departmentsInfo = await orgChartDB.getData('info_departments')
      const jobpositionsInfo = await orgChartDB.getData('info_jobpositions')
      const jobtitlesInfo = await orgChartDB.getData('info_jobtitles')
      const managersInfo = await orgChartDB.getData('info_managers')
      const eTagInfo = await orgChartDB.getData('info_etag')

      const allData: AllDataResponse | null = await orgChartDB.getAllData()

      if (allData != null) {
        const users = allData.dataList

        const orgChartData: OrgChartData = {
          departments: departmentsInfo?.data ?? [],
          jobpositions: jobpositionsInfo?.data ?? [],
          jobtitles: jobtitlesInfo?.data ?? [],
          managers: managersInfo?.data ?? [],
          users,
        }

        // eTag가 바뀐 경우(최초 포함)에만 리스너를 notify.
        // 이 필터링이 없으면 findContact → loadData 경로에서 매번 notify가 발생해
        // knownSenderCache가 자기파괴 사이클에 빠진다.
        //
        // force=true(캐시 우선 표시 경로)는 eTag 억제를 우회해 즉시 화면에 표시하되,
        // 실데이터(users>0)가 있을 때만 notify한다. 빈 캐시(최초 로그인 등)는 억제하여
        // EmptyData 깜빡임을 막고 스피너를 유지한다. force는 Tree.reload만 사용하므로
        // findContact 경로의 자기파괴 방지 로직에는 영향이 없다.
        const currentETag = (eTagInfo?.data ?? null) as string | null
        const shouldNotify =
          options?.force === true ? users.length > 0 : currentETag !== this.lastNotifiedETag
        if (shouldNotify) {
          this.lastNotifiedETag = currentETag
          this.changeListener.notify(orgChartData)
        }

        return orgChartData
      }
    } catch (e) {
      console.log('OrgChartHelper.loadData error : ', e)
    }
    return null
  }

  getAvatarUrl = (originData: User | UserData): string | null => {
    const data = 'data' in originData ? originData.data : originData
    const { profile_photo_url: profilePhotoUrl } = data

    let imgSrc: string | null = null
    if (
      profilePhotoUrl == null ||
      profilePhotoUrl.trim().length === 0 ||
      profilePhotoUrl.indexOf('my_default') > -1
    ) {
      imgSrc = null
    } else {
      imgSrc = profilePhotoUrl
    }
    return imgSrc
  }

  getAvatarUrlByEmail = async (email: string): Promise<string | null> => {
    const userData = await this.findContact(email)
    let url: string | null = null
    if (userData != null) {
      url = this.getAvatarUrl(userData)
    }
    return url
  }

  // 부서 멤버(user_id)는 존재하는데 사용자 레코드가 비어있는 캐시 불일치를 감지한다.
  // 부분 저장 실패, 외부(DevTools) clear, 과거 경합 등으로 user 레코드만 사라지면
  // departments는 남아 있어 "부서명만 표시 + user not found"가 된다.
  private hasMembersButNoUsers = (data: OrgChartData): boolean => {
    if (data.users != null && data.users.length > 0) {
      return false
    }
    const memberCount = (data.departments ?? []).reduce(
      (sum, department) => sum + (department.members != null ? department.members.length : 0),
      0,
    )
    return memberCount > 0
  }

  // info_etag만 저장되고 나머지 info_* 메타데이터는 저장되지 못한 오염 캐시를 감지한다.
  // 304 경로는 info_etag가 존재함이 보장되므로(If-None-Match 전송됨), 핵심 메타 레코드인
  // info_departments의 "레코드 부재"만으로 오염을 판정한다.
  //  - 오염 캐시: 레코드가 아예 없어 getData가 null을 반환한다.
  //  - 정당한 빈 조직: saveData가 putData('info_departments', [])로 빈 배열 레코드를 저장하므로
  //    getData가 { data: [] }(non-null)를 반환한다 → 오염으로 오판하지 않는다.
  private isCorruptedCache = async (): Promise<boolean> => {
    const departmentsInfo = await orgChartDB.getData('info_departments')
    return departmentsInfo == null
  }

  loadNewData = async (eTag?: string, retriedOnMismatch = false): Promise<OrgChartData | null> => {
    let ret: OrgChartData | null = null

    try {
      const headers: Record<string, string> = {}

      if (eTag != null) {
        headers['If-None-Match'] = eTag
      }

      const res: ApiResponse = await orgChartClient.get('/org_chart/get_org_chart', {}, { headers })
      console.log('get_org_chart : ', res)
      const { origin, data: resData } = res

      if (origin != null) {
        const { data } = resData || {}
        if (origin.status === 200) {
          const { headers: resHeader } = origin
          const resETag = resHeader.get('Etag')
          console.log('eTag : ', resETag)
          ret = await this.saveData(data || null, resETag || undefined)
        } else if (origin.status === 304) {
          ret = await this.loadData()
          // 304는 saveData를 거치지 않아 깨진 캐시가 자가 회복되지 않는다. 두 종류의 손상을
          // 감지하면 eTag 없이 1회 강제 재요청(200)해 캐시를 재구축한다.
          //  - hasMembersButNoUsers: 부서 멤버는 있으나 사용자 레코드만 사라진 불일치
          //  - isCorruptedCache: info_etag만 저장되고 나머지 info_* 메타가 없는 오염
          if (
            !retriedOnMismatch &&
            ret != null &&
            (this.hasMembersButNoUsers(ret) || (await this.isCorruptedCache()))
          ) {
            console.warn('OrgChart: 캐시 불일치/오염 감지 → eTag 무시하고 강제 재로드')
            ret = await this.loadNewData(undefined, true)
          }
        }
      }
    } catch (e) {
      console.error('loadNewData error : ', e)
      ret = null
    }

    return ret
  }

  findContact = async (email: string, forceReload?: boolean): Promise<UserData | null> => {
    let allData: OrgChartData | null = null
    if (forceReload === true) {
      allData = await this.startLoading()
    } else {
      allData = await this.loadData()
    }

    if (allData != null) {
      const { users } = allData
      const userDBData = users.find((dbData) => {
        const { account, emails } = dbData.data
        if (account != null && account === email) {
          return true
        }
        if (emails != null && emails.length > 0) {
          return emails.find((emailItem) => emailItem.address === email)
        }
        return false
      })
      if (userDBData != null) {
        return userDBData.data
      }
    }

    return null
  }

  getSubData = async (userId: string): Promise<SubData> => {
    const departments: DBResponse | null = await orgChartDB.getData('info_departments')
    if (departments == null) return {}

    let member: DepartmentMember | undefined = undefined
    const department = departments.data.find((department: Department) => {
      member = department.members.find((user) => user.user_id === userId)
      return member != null
    })
    if (department == null) {
      return {}
    }

    const jobs: DBResponse | null = await orgChartDB.getData('info_jobtitles')
    if (jobs == null) return { department }
    const job = jobs.data.find((job: JobTitle) => job.id === member?.jobtitle_id)

    const jobpositions: DBResponse | null = await orgChartDB.getData('info_jobpositions')
    const jobposition = jobpositions?.data?.find(
      (jobposition: JobPosition) => jobposition.id === job?.jobposition_id,
    )

    return {
      department,
      job,
      jobposition,
    }
  }
}

const helper = new OrgChartHelper()

export default helper
